// ServicePreferredSources.tsx
// Design: Dark navy (#0F1419) base, electric teal (#00D4AA) and amber (#FF8C00) accents
// Pillar: aeo-geo | Target keyword: "preferred sources strategy AI answers"
// Schema: Service, FAQPage, BreadcrumbList, Speakable

import { useEffect } from "react";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";
import Header from "@/components/Header";
import Breadcrumbs from "@/components/Breadcrumbs";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SchemaScript from "@/components/SchemaScript";
const HERO_URL = "/media/hero-preferred-sources-strategy_05a26152.jpg";

const deliverables = [
  {
    icon: "🧠",
    title: "Entity Authority Mapping",
    desc: "We audit how AI systems currently represent your brand, identify entity gaps, and build a structured knowledge graph that positions you as the definitive source on your core topics.",
  },
  {
    icon: "📐",
    title: "Answer-First Content Architecture",
    desc: "Every page and post is restructured so the first 40 words answer the query directly. AI systems extract and cite content that leads with the answer, not the preamble.",
  },
  {
    icon: "🏷️",
    title: "Schema & Structured Data Deployment",
    desc: "FAQPage, HowTo, Speakable, BlogPosting, and Service schemas are deployed across your entire content estate to give AI systems machine-readable signals for citation selection.",
  },
  {
    icon: "🔗",
    title: "Corroboration Network Building",
    desc: "We identify and secure citations, mentions, and co-occurrence links from authoritative third-party sources that corroborate your entity claims and increase AI citation probability.",
  },
  {
    icon: "📊",
    title: "Citation Tracking & Monitoring",
    desc: "We set up monitoring across ChatGPT, Gemini, Perplexity, and Google AI Mode to track when and how your brand is cited, which queries trigger citations, and where gaps remain.",
  },
  {
    icon: "🔄",
    title: "Continuous Optimization Loop",
    desc: "Monthly audits of AI citation patterns, competitor preferred source analysis, and content gap identification keep your preferred source status ahead of algorithm updates.",
  },
];

const comparison = [
  { aspect: "Content goal", traditional: "Rank on page 1", preferred: "Be cited in AI answer" },
  { aspect: "Success metric", traditional: "Click-through rate", preferred: "Citation frequency" },
  { aspect: "Content structure", traditional: "Keyword density", preferred: "Answer-first paragraphs" },
  { aspect: "Authority signal", traditional: "Backlink count", preferred: "Entity corroboration" },
  { aspect: "Schema priority", traditional: "Title + meta tags", preferred: "FAQPage + Speakable + HowTo" },
  { aspect: "Visibility channel", traditional: "Blue link results", preferred: "AI Overviews, ChatGPT, Gemini" },
];

const process = [
  {
    step: "01",
    title: "AI Citation Audit",
    desc: "We query 5 major AI systems with your target keywords and document exactly how your brand is currently represented, cited, or absent. This baseline defines the gap.",
  },
  {
    step: "02",
    title: "Entity & Schema Architecture",
    desc: "We design your entity graph: the topics you own, the relationships between them, and the schema types needed to make each claim machine-readable to AI citation engines.",
  },
  {
    step: "03",
    title: "Content & Corroboration Sprint",
    desc: "We restructure existing content for answer-first delivery, deploy schemas site-wide, and execute a targeted outreach campaign to build third-party corroboration signals.",
  },
  {
    step: "04",
    title: "Monitor, Measure, Iterate",
    desc: "Monthly citation tracking reports show which queries now cite you, which competitors are gaining ground, and what content to produce next to maintain preferred source status.",
  },
];

const faqs = [
  {
    q: "What is a preferred source in AI answers?",
    a: "A preferred source is a brand or website that AI systems such as ChatGPT, Gemini, Perplexity, and Google AI Mode consistently cite when answering queries related to that brand's area of expertise. Preferred source status is earned through a combination of entity authority, answer-first content structure, structured data markup, and third-party corroboration.",
  },
  {
    q: "How does preferred source strategy differ from traditional SEO?",
    a: "Traditional SEO optimizes for ranking in blue-link search results, measured by position and click-through rate. Preferred source strategy optimizes for citation in AI-generated answers, where there are no rankings, only citations. The signals that drive AI citation include entity clarity, answer-first content structure, FAQPage and Speakable schema, and corroboration from authoritative third-party sources.",
  },
  {
    q: "Which AI systems does this strategy target?",
    a: "Our preferred source strategy targets Google AI Overviews, Google AI Mode, ChatGPT (with browsing), Perplexity, Microsoft Copilot, and Gemini. Each system has slightly different citation signals, and our approach addresses all of them through a unified entity authority and structured data framework.",
  },
  {
    q: "How long does it take to become a preferred source?",
    a: "Initial citation improvements typically appear within 6 to 12 weeks of deploying schema changes and answer-first content restructuring. Full preferred source status, where your brand is consistently cited across multiple query clusters, typically takes 3 to 6 months depending on the competitiveness of your topic area and the current state of your entity authority.",
  },
  {
    q: "Can I track whether AI systems are citing my brand?",
    a: "Yes. We set up a citation monitoring workflow using manual query testing across 5 AI platforms, Google Search Console AI Overview impression data, and third-party tools such as Semrush and BrightEdge where available. Monthly reports document citation frequency, query triggers, and competitor citation gaps.",
  },
  {
    q: "Do I need to create new content or can existing content be optimized?",
    a: "Both. We start by restructuring your highest-traffic existing pages for answer-first delivery and deploying schema markup, which delivers the fastest citation gains. We then identify content gaps where no existing page answers a high-value AI query and build new spoke content to fill those gaps within your pillar architecture.",
  },
  {
    q: "How does preferred source strategy work alongside AEO and GEO?",
    a: "Preferred source strategy is the outcome; AEO (Answer Engine Optimization) and GEO (Generative Engine Optimization) are the disciplines that achieve it. AEO focuses on structured data and answer-first content. GEO focuses on entity authority and corroboration signals. Together they build the conditions for consistent AI citation, which is what preferred source status means in practice.",
  },
  {
    q: "Which figures on this page can I cite as a measured result?",
    a: "Cite the comparison table: a preferred source is judged by citation, not by a blue-link position. The 60 percent, one-to-two sources, 73 percent, 3–6x, and 40 percent-plus lines are printed without a named study or a named client. They are not an Integrated.Social case result. First movement is 6 to 12 weeks on this page. Consistent preferred-source status is 3 to 6 months. That is a different clock from a first citation on the SEO, AEO and GEO page.",
  },
];

export default function ServicePreferredSources() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/preferred-sources-strategy/#service",
        "name": "Preferred Sources Strategy",
        "alternateName": "AI Citation Strategy",
        "description": "A senior-led preferred sources strategy for B2B: entity authority, answer-first content and structured data that make your brand easier for AI systems to understand and cite.",
        "provider": { "@id": "https://integrated.social/#organization" },
        "serviceType": "AI Citation Optimization",
        "areaServed": ["GB", "US"],
        "url": "https://integrated.social/services/preferred-sources-strategy/",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "url": "https://calendly.com/modielnadi/30min"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": "Preferred Sources Strategy", "item": "https://integrated.social/services/preferred-sources-strategy/" }
        ]
      },
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/preferred-sources-strategy/",
        "url": "https://integrated.social/services/preferred-sources-strategy/",
        "name": "AI Preferred Sources Strategy: Make Your Brand Easier to Cite",
        "headline": "AI Preferred Sources Strategy: Make Your Brand Easier to Cite",
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/services/preferred-sources-strategy/#service" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" }
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/preferred-sources-strategy/#faq",
        "mainEntity": faqs.map(({ q, a }) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": a }
        }))
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="AI Preferred Sources Strategy: Easier for ChatGPT to Cite"
        description="AI preferred sources strategy for B2B: entity authority and answer-first content that make your brand easier for ChatGPT, Perplexity and Gemini to cite."
        canonical="https://integrated.social/services/preferred-sources-strategy/"
        ogImage="https://integrated.social/media/hero-preferred-sources-strategy_05a26152.jpg"
      />
      <main className="bg-[#0F1419] text-white min-h-screen">
      {/* JSON-LD Schema */}
      <SchemaScript schema={JSON.stringify(schemaData)} />

      <Header />
      <div className="container px-4 pt-20">
        <Breadcrumbs
          includeSchema={false}
          items={[
            { label: "Services", href: "/services/" },
            { label: "Preferred Sources Strategy" },
          ]}
        />
      </div>

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_URL})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1419]/95 via-[#0F1419]/80 to-[#0F1419]/40" />
        <div className="relative container py-24 lg:py-32">
          <div className="max-w-2xl">
            <p className="text-mono text-[#00D4AA] mb-4 tracking-widest text-xs uppercase">
              AEO / GEO / AI Citation Strategy
            </p>
            <h1 className="text-4xl lg:text-6xl font-serif font-bold leading-tight mb-6">
              AI Preferred Sources Strategy:<br />
              <span className="text-[#FF8C00] italic">Make Your Brand Easier</span><br />
              to Cite
            </h1>
            <p className="speakable-intro text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
              Preferred Sources Strategy builds your entity authority, restructures content for answer-first delivery,
              and deploys the schema infrastructure that makes ChatGPT, Gemini, Perplexity, and Google AI Mode
              choose your brand as the trusted source for your category.
            </p>
            <div className="flex flex-wrap gap-4">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                href="https://calendly.com/modielnadi/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#FF8C00] text-[#0F1419] px-8 py-3.5 font-semibold text-sm rounded-sm hover:bg-[#E67E00] transition-colors btn-press" onClick={() => { trackCalendlyClick("service_preferred_sources"); trackSectionCtaClick({ section_name: "service_preferred_sources_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
              >
                Get Your AI Citation Audit
              </a>
              <a
                href="/how-b2b-brands-appear-in-chatgpt-answers/"
                className="border border-[#00D4AA]/40 text-[#00D4AA] px-8 py-3.5 font-semibold text-sm rounded-sm hover:border-[#00D4AA] hover:bg-[#00D4AA]/5 transition-colors"
              >
                How B2B brands appear in ChatGPT answers
              </a>
            </div>
          </div>
        </div>
      </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="Preferred Sources Strategy for B2B. We build entity authority, structured data architecture and answer-first content that make your brand easier for ChatGPT, Gemini, Perplexity and Google AI Mode to understand and cite."
            title="Preferred Source Strategy | AI Citation Authority"
            readingTime={5}
          />
        </div>

      {/* The Problem */}
      <section className="py-20 bg-[#111827]">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-5">
              AI Answers Have <span className="text-[#FF8C00]">No Page 2</span>
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              When a buyer asks ChatGPT or Gemini "which agency should I use for B2B AI marketing," the AI cites one or two sources.
              Either your brand is cited, or a competitor is. There is no position 4 to optimize toward.
              Preferred source status is binary, and it is won before the query is asked.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { stat: "60%", label: "of B2B searches now trigger an AI Overview or AI Mode answer before any blue links", color: "#FF8C00" },
              { stat: "1–2", label: "sources are cited per AI answer on average - the rest are invisible", color: "#00D4AA" },
              { stat: "73%", label: "of buyers trust AI-cited sources more than paid ads or organic results", color: "#FF8C00" },
            ].map(({ stat, label, color }) => (
              <div key={stat} className="bg-[#0F1419] border border-white/5 p-8 text-center">
                <p className="stat-number text-5xl mb-3" style={{ color }}>{stat}</p>
                <p className="text-white/50 text-sm leading-relaxed">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-20 bg-[#0F1419]">
        <div className="container">
          <div className="max-w-2xl mb-14">
            <p className="text-mono text-[#00D4AA] mb-3 tracking-widest text-xs uppercase">What We Build</p>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-4">
              Six Pillars of Preferred Source Status
            </h2>
            <p className="text-white/50 leading-relaxed">
              Preferred source status is not a single tactic. It is the outcome of six interconnected systems
              working together to signal authority, clarity, and trustworthiness to AI citation engines.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliverables.map(({ icon, title, desc }) => (
              <div key={title} className="bg-[#111827] border border-white/5 p-7 hover:border-[#00D4AA]/20 transition-colors group">
                <span className="text-3xl mb-4 block">{icon}</span>
                <h3 className="text-white font-semibold text-base mb-3 group-hover:text-[#00D4AA] transition-colors">{title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-[#111827]">
        <div className="container">
          <div className="max-w-2xl mb-14">
            <p className="text-mono text-[#FF8C00] mb-3 tracking-widest text-xs uppercase">Strategy Shift</p>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-4">
              Traditional SEO vs. Preferred Source Strategy
            </h2>
            <p className="text-white/50 leading-relaxed">
              The signals that earn AI citations are fundamentally different from those that drive blue-link rankings.
              Both matter, but they require different architectures. See the <a href="/services/seo-aeo-geo/" className="text-[#00D4AA] hover:underline">SEO, AEO and GEO</a> service for the disciplines behind the outcome.
            </p>
          </div>
          <div className="max-w-3xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 pr-6 text-white/40 font-medium w-1/3">Dimension</th>
                  <th className="text-left py-3 pr-6 text-white/40 font-medium w-1/3">Traditional SEO</th>
                  <th className="text-left py-3 text-[#00D4AA] font-medium w-1/3">Preferred Source Strategy</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map(({ aspect, traditional, preferred }) => (
                  <tr key={aspect} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                    <td className="py-4 pr-6 text-white/60 font-medium">{aspect}</td>
                    <td className="py-4 pr-6 text-white/40">{traditional}</td>
                    <td className="py-4 text-[#00D4AA]/80">{preferred}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#0F1419]">
        <div className="container">
          <div className="max-w-2xl mb-14">
            <p className="text-mono text-[#00D4AA] mb-3 tracking-widest text-xs uppercase">How It Works</p>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-4">
              The Four-Phase Citation Framework
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <p className="font-serif text-6xl text-[#00D4AA]/10 mb-2">{step}</p>
                <h3 className="text-white font-semibold text-base mb-3">{title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 bg-[#111827]">
        <div className="container">
          <div className="max-w-2xl mb-14">
            <p className="text-mono text-[#FF8C00] mb-3 tracking-widest text-xs uppercase">Measurable Outcomes</p>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-4">
              What Preferred Source Status Delivers
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { metric: "3-6x", label: "increase in AI Overview impressions within 90 days of schema deployment" },
              { metric: "40%+", label: "of target queries citing your brand across at least 2 AI platforms after 6 months" },
              { metric: "Zero-click", label: "brand visibility: buyers learn your name before they visit your site" },
              { metric: "Compounding", label: "authority: each citation increases the probability of the next one" },
            ].map(({ metric, label }) => (
              <div key={metric} className="bg-[#0F1419] border border-white/5 p-7">
                <p className="text-3xl font-bold text-[#FF8C00] mb-3">{metric}</p>
                <p className="text-white/50 text-sm leading-relaxed">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-16 bg-[#0F1419] border-t border-white/5">
        <div className="container">
          <p className="text-mono text-[#00D4AA] mb-6 tracking-widest text-xs uppercase">Related Services and Resources</p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { label: "SEO + AEO/GEO Service", href: "/services/seo-aeo-geo/", desc: "The technical foundation that enables preferred source status" },
              { label: "Google AI Mode Optimization", href: "/services/google-ai-mode-optimisation/", desc: "Specific optimization for Google's AI Mode citation signals" },
              { label: "How B2B brands appear in ChatGPT answers", href: "/aeo/", desc: "Free guides on answer engine optimization and AI citation strategy" },
              { label: "How to Write for ChatGPT Citations", href: "/blog/write-content-cited-chatgpt-answers/", desc: "Step-by-step guide to answer-first content structure" },
              { label: "Zero-Click Search B2B Strategy", href: "/blog/zero-click-searches-how-to-win/", desc: "How to win visibility when buyers never click through" },
              { label: "Agentic AI Lead Gen", href: "/services/gemini-agentic-ai/", desc: "Multi-agent systems that act on your preferred source authority" },
            ].map(({ label, href, desc }) => (
              <a
                key={href}
                href={href}
                className="block bg-[#111827] border border-white/5 p-5 hover:border-[#00D4AA]/20 transition-colors group"
              >
                <p className="text-white/90 group-hover:text-[#00D4AA] font-medium text-sm mb-1 transition-colors">{label}</p>
                <p className="text-white/40 text-xs">{desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#111827] faq-section">
        <div className="container max-w-3xl">
          <p className="text-mono text-[#FF8C00] mb-3 tracking-widest text-xs uppercase">FAQ</p>
          <h2 className="text-3xl font-serif font-bold mb-12">
            Preferred Sources Strategy: Common Questions
          </h2>
          <div className="space-y-0 divide-y divide-white/5">
            {faqs.map(({ q, a }) => (
              <div key={q} className="py-7">
                <h3 className="text-white font-semibold text-base mb-3">{q}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F1419]">
        <div className="container max-w-2xl text-center">
          <h2 className="text-3xl lg:text-4xl font-serif font-bold mb-5">
            Ready to Become the <span className="text-[#00D4AA]">Preferred Source</span> in Your Category?
          </h2>
          <p className="text-white/55 text-lg mb-8 leading-relaxed">
            We start with a free AI Citation Audit: 30 minutes, no pitch deck, just an honest assessment of
            where your brand stands in AI answers today and what it would take to change that. This is different from the <a href="/free-ai-growth-audit/" className="text-[#00D4AA] hover:underline">60-second self-serve score</a>.
          </p>
          <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
            href="https://calendly.com/modielnadi/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#FF8C00] text-[#0F1419] px-10 py-4 font-semibold text-sm rounded-sm hover:bg-[#E67E00] transition-colors btn-press" onClick={() => { trackCalendlyClick("service_preferred_sources"); trackSectionCtaClick({ section_name: "service_preferred_sources_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
          >
            Book Your Free AI Citation Audit
          </a>
        </div>
      </section>
        <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
        </main>

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  See exactly where your brand appears in Google AI Mode, ChatGPT, Perplexity, and Gemini. Free AI Visibility Score with a scored report and gap analysis.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_preferred_sources_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalised</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_preferred_sources"); trackSectionCtaClick({ section_name: "service_preferred_sources_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="Preferred Sources Strategy"
          canonicalUrl="https://integrated.social/services/preferred-sources-strategy/"
          excerpt="Build the authority signals that make your brand the preferred source AI systems cite first."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
